// Native disk-backed live replay recording.
//
// A browser (WHIP) live is NOT recorded by Cloudflare, so we record the broadcast
// locally and upload it as the replay. Keeping a multi-hour recording entirely in
// RAM crashes the phone (OOM), so on native we STREAM the MediaRecorder chunks to a
// cache file as they arrive, then hand that file to the native background uploader.
//
// Disk hygiene: the file is deleted right after a successful upload, and the whole
// replay cache folder is wiped on app start (cleanupReplayCache) so nothing ever
// accumulates across lives.
import { Capacitor } from "@capacitor/core";

const DIR = "yalola-replays"; // under Directory.Cache
const FLUSH_BYTES = 6 * 1024 * 1024; // buffer ~6 MB before writing to disk

export const isNativePlatform = () => Capacitor.isNativePlatform();

let _fs = null;
async function fsMod() {
  if (!_fs) _fs = await import("@capacitor/filesystem");
  return _fs;
}

function blobToBase64(blob) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onloadend = () => resolve(String(r.result || "").split(",")[1] || "");
    r.onerror = reject;
    r.readAsDataURL(blob);
  });
}

// Delete EVERY residual replay file. Called at app start (best-effort, native only).
export async function cleanupReplayCache() {
  if (!isNativePlatform()) return;
  try {
    const { Filesystem, Directory } = await fsMod();
    try { await Filesystem.rmdir({ path: DIR, directory: Directory.Cache, recursive: true }); } catch { /* nothing to clean */ }
  } catch { /* filesystem unavailable */ }
}

// Create a streamed writer that appends recorder chunks to a cache file.
// Returns a handle, or null if the disk can't be written (caller falls back to RAM).
export async function createReplayFileWriter(filename) {
  if (!isNativePlatform()) return null;
  try {
    const { Filesystem, Directory } = await fsMod();
    try { await Filesystem.mkdir({ path: DIR, directory: Directory.Cache, recursive: true }); } catch { /* already exists */ }
    const path = `${DIR}/${filename}`;
    await Filesystem.writeFile({ path, directory: Directory.Cache, data: "", recursive: true });
    let healthy = true;
    let buf = [];
    let bufBytes = 0;
    let chain = Promise.resolve(); // serialize ALL appends → chunks land in order

    const appendPart = async (part) => {
      if (!healthy) return;
      try {
        const b64 = await blobToBase64(part);
        if (b64) await Filesystem.appendFile({ path, directory: Directory.Cache, data: b64 });
      } catch {
        healthy = false; // disk full / write error → stop feeding this file
      }
    };

    // Snapshot the buffer and enqueue its write onto the serialized chain.
    const flush = () => {
      if (!buf.length) return chain;
      const part = new Blob(buf);
      buf = []; bufBytes = 0;
      chain = chain.then(() => appendPart(part));
      return chain;
    };

    return {
      path,
      healthy: () => healthy,
      async push(blob) {
        if (!healthy || !blob || !blob.size) return;
        buf.push(blob); bufBytes += blob.size;
        if (bufBytes >= FLUSH_BYTES) await flush();
      },
      async finish() {
        flush();       // enqueue any remaining buffered bytes
        await chain;   // wait for EVERY append to land, in order
        try {
          const { uri } = await Filesystem.getUri({ path, directory: Directory.Cache });
          const st = await Filesystem.stat({ path, directory: Directory.Cache }).catch(() => ({}));
          return { uri, size: st?.size || 0 };
        } catch { return null; }
      },
      async remove() {
        try { await Filesystem.deleteFile({ path, directory: Directory.Cache }); } catch { /* already gone */ }
      },
    };
  } catch {
    return null;
  }
}
